import { useMemo, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { Pencil, Plus } from 'lucide-react';
import { useNavigate } from 'react-router';
import { graphql } from '@/gql';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { SortableList } from '@/components/ui/SortableList';
import { Switch } from '@/components/ui/Switch';
import { FormField } from '@/components/ui/FormLayout';
import { downloadAuthenticatedCsv } from '@/lib/downloadCsv';

const CategoriesDoc = graphql(/* GraphQL */ `
  query Categories {
    categories(includeInactive: true) {
      id
      name
      sort
      isActive
      showInMenu
      productCount
    }
  }
`);

const CreateCategoryDoc = graphql(/* GraphQL */ `
  mutation CreateCategory($input: CreateCategoryInput!) {
    createCategory(input: $input) {
      id
      name
    }
  }
`);

const ReorderCategoriesDoc = graphql(/* GraphQL */ `
  mutation ReorderCategories($entity: SortableEntity!, $orderedIds: [ID!]!) {
    reorder(entity: $entity, orderedIds: $orderedIds)
  }
`);

const ExportCategoriesCsvDoc = graphql(/* GraphQL */ `
  query ExportCategoriesCsv {
    exportCategoriesCsv {
      url
      expiresAt
    }
  }
`);

interface CategoryRow {
  id: string;
  name: string;
  sort: number;
  isActive: boolean;
  showInMenu: boolean;
  productCount: number;
}

export function CategoriesListPage() {
  const apollo = useApolloClient();
  const navigate = useNavigate();
  const { data, previousData, loading, refetch } = useQuery(CategoriesDoc);
  const categoriesData = data ?? previousData;
  const [createCategory, { loading: creating }] = useMutation(CreateCategoryDoc);
  const [reorder] = useMutation(ReorderCategoriesDoc);

  const [modalOpen, setModalOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newShowInMenu, setNewShowInMenu] = useState(true);
  const [newIsActive, setNewIsActive] = useState(true);
  const [newIsDoor, setNewIsDoor] = useState(false);

  const categories: CategoryRow[] = useMemo(
    () =>
      (categoriesData?.categories ?? []).map((c) => ({
        id: c.id,
        name: c.name,
        sort: c.sort,
        isActive: c.isActive,
        showInMenu: c.showInMenu,
        productCount: c.productCount,
      })),
    [categoriesData],
  );

  const handleReorder = async (orderedIds: string[]) => {
    await reorder({ variables: { entity: 'CATEGORY', orderedIds } });
    await refetch();
  };

  const submitCreate = async () => {
    if (creating || !newName.trim()) return;
    await createCategory({
      variables: {
        input: {
          name: newName.trim(),
          showInMenu: newShowInMenu,
          isActive: newIsActive,
          isDoor: newIsDoor,
        },
      },
    });
    setNewName('');
    setNewShowInMenu(true);
    setNewIsActive(true);
    setNewIsDoor(false);
    setModalOpen(false);
    await refetch();
  };

  const handleExport = async () => {
    const { data: exportData } = await apollo.query({
      query: ExportCategoriesCsvDoc,
      fetchPolicy: 'network-only',
    });
    if (exportData?.exportCategoriesCsv?.url) {
      await downloadAuthenticatedCsv(exportData.exportCategoriesCsv.url);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Categories
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Drag to reorder the full list. Open a category to edit products, menu visibility
            and active state.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleExport}>
            Export CSV
          </Button>
          <Button
            id="admin.categories.new"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => setModalOpen(true)}
          >
            New category
          </Button>
        </div>
      </div>

      <Card padded={false}>
        {loading && categories.length === 0 ? (
          <p className="p-6 text-sm text-gray-500">Loading categories…</p>
        ) : categories.length === 0 ? (
          <p className="p-6 text-sm text-gray-500">
            No categories yet. Add one to start grouping products.
          </p>
        ) : (
          <div className="p-4">
            <SortableList
              items={categories}
              onReorder={handleReorder}
              renderItem={(item, { dragHandle }) => (
                <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 dark:border-white/10 dark:bg-gray-900">
                  {dragHandle}
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-gray-900 dark:text-white">
                      {item.name}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      <Badge tone="neutral">{item.productCount} products</Badge>
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
                    <Badge tone={item.showInMenu ? 'info' : 'neutral'}>
                      {item.showInMenu ? 'In menu' : 'Hidden'}
                    </Badge>
                    <Badge tone={item.isActive ? 'success' : 'neutral'}>
                      {item.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                    <Button
                      id={`admin.categories.edit.${item.name}`}
                      variant="ghost"
                      size="sm"
                      leftIcon={<Pencil className="h-4 w-4" />}
                      onClick={() => navigate(`/categories/${item.id}`)}
                    >
                      Edit
                    </Button>
                  </div>
                </div>
              )}
            />
          </div>
        )}
      </Card>

      <Modal
        open={modalOpen}
        onClose={() => (creating ? null : setModalOpen(false))}
        title="New category"
        footer={
          <>
            <Button
              variant="ghost"
              type="button"
              onClick={() => setModalOpen(false)}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button
              id="admin.categories.create"
              type="button"
              onClick={submitCreate}
              disabled={creating || !newName.trim()}
            >
              {creating ? 'Saving…' : 'Create'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormField label="Name" required>
            <Input
              id="admin.categories.name"
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              required
            />
          </FormField>
          <FormField label="Show in POS menu">
            <Switch
              checked={newShowInMenu}
              onChange={(event) => setNewShowInMenu(event.target.checked)}
              label={newShowInMenu ? 'Visible' : 'Hidden'}
            />
          </FormField>
          <FormField label="Active">
            <Switch
              checked={newIsActive}
              onChange={(event) => setNewIsActive(event.target.checked)}
              label={newIsActive ? 'Active' : 'Inactive'}
            />
          </FormField>
          <FormField
            label="Door ticket sales"
            description="When on, this category shows one extra button per door ticket phase for any night whose doors are currently open. Tickets go into the normal basket alongside drinks."
          >
            <Switch
              id="admin.categories.isDoor"
              checked={newIsDoor}
              onChange={(event) => setNewIsDoor(event.target.checked)}
              label={newIsDoor ? 'On' : 'Off'}
            />
          </FormField>
        </div>
      </Modal>
    </div>
  );
}
